/**
 * Foreign-key source for the FK-driven code actions ("Add JOIN ON …",
 * "Add missing table to FROM").
 *
 * The fixes used to read foreign keys ONLY from the Object Explorer tree —
 * the `key` nodes under a table's `keys-folder`. Those nodes exist only after
 * the user has expanded that table's Keys folder, so whether a quick-fix was
 * offered depended on what happened to be expanded in the explorer. That is
 * not acceptable behaviour for an editor feature: what the user has clicked
 * in a side panel must not decide which fixes exist.
 *
 * The language service already introspects foreign keys for every engine and
 * publishes them on the schema bus (`SchemaSnapshot.foreignKeys`, the same
 * edges autocomplete's JOIN candidates use), lazily per (connection,
 * database). So the fixes now take FKs from BOTH sources — the tree when its
 * keys are loaded, the snapshot otherwise (or in addition) — and when the
 * snapshot is cold they kick the engine's schema load (fire-and-forget, the
 * same contract the resolvers use on a cold cache). Monaco recomputes code
 * actions on the next cursor move / edit, so the lightbulb appears once the
 * snapshot lands, without anything expanded in the explorer.
 */

import type { DatabaseEngineId } from '@/core/engines';
import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { ensureMssqlTables } from '@/features/connections/services/language-services/mssqlTableCache';
import { ensurePgSchema } from '@/features/connections/services/language-services/pgSchemaCache';
import { ensureMysqlSchema } from '@/features/connections/services/language-services/mysqlSchemaCache';
import { ensureOracleSchema } from '@/features/connections/services/language-services/oracleSchemaCache';
import { ensureSqliteSchema } from '@/features/connections/services/language-services/sqliteSchemaCache';
import type { FkEdge } from '@/core/engines/intellisenseTypes';

export type ForeignKeyPair = { readonly localCol: string; readonly remoteCol: string };

export type SnapshotFkLookup = {
  readonly connectionId: string;
  readonly database: string | null;
  /** Table the FK constraint lives on (the referencing side). */
  readonly localName: string;
  /** Lower-cased schema to enforce on the local side, or `undefined` to accept any. */
  readonly localSchemaForMatch?: string;
  /** Table the FK references. */
  readonly remoteName: string;
  /** Lower-cased schema to enforce on the referenced side, or `undefined` to accept any. */
  readonly remoteSchemaForMatch?: string;
  /** Engine default schema, lower-cased — an FK whose schema label equals it is never rejected. */
  readonly defaultSchema: string;
};

/**
 * FKs on `localName` that reference `remoteName`, read from the schema-bus
 * snapshot. One entry per constraint (multi-column FKs surface as one entry
 * with N pairs), mirroring the tree walkers' return shape. Empty when the
 * snapshot is cold — see `requestForeignKeyLoad`.
 */
export function findForeignKeyPairsInSnapshot(
  lookup: SnapshotFkLookup,
): readonly (readonly ForeignKeyPair[])[] {
  const snapshot = getSchemaBusSnapshot(lookup.connectionId, lookup.database ?? undefined);
  if (!snapshot) return [];

  const local = lookup.localName.toLowerCase();
  const remote = lookup.remoteName.toLowerCase();
  const byConstraint = new Map<string, ForeignKeyPair[]>();
  snapshot.foreignKeys.forEach((edge, index) => {
    if (edge.fromTable.toLowerCase() !== local) return;
    if (edge.toTable.toLowerCase() !== remote) return;
    if (!schemaMatches(edge.fromSchema, lookup.localSchemaForMatch, lookup.defaultSchema)) return;
    if (!schemaMatches(edge.toSchema, lookup.remoteSchemaForMatch, lookup.defaultSchema)) return;
    // Snapshot edges may be one per column; group them back into constraints
    // by name so a composite FK yields one N-pair entry. An unnamed edge is
    // its own constraint.
    const key = edge.name ? `named:${edge.name}` : `anon:${index}`;
    const pairs = byConstraint.get(key) ?? [];
    for (const pair of edgePairs(edge)) pairs.push(pair);
    byConstraint.set(key, pairs);
  });
  return Array.from(byConstraint.values()).filter((pairs) => pairs.length > 0);
}

/**
 * Permissive schema match — the Slice 6 / 11 / 12 rule the tree walkers apply:
 * enforce a schema only when the caller has one to enforce AND the edge's
 * label disagrees with both it and the engine default (three of five engines
 * label schemas differently from `defaultSchemaForEngine`).
 */
function schemaMatches(
  edgeSchema: string | undefined,
  schemaForMatch: string | undefined,
  defaultSchema: string,
): boolean {
  if (!schemaForMatch || !edgeSchema) return true;
  const edge = edgeSchema.toLowerCase();
  return edge === schemaForMatch || edge === defaultSchema;
}

function edgePairs(edge: FkEdge): readonly ForeignKeyPair[] {
  const count = Math.min(edge.fromColumns.length, edge.toColumns.length);
  const pairs: ForeignKeyPair[] = [];
  for (let i = 0; i < count; i++) {
    pairs.push({ localCol: edge.fromColumns[i], remoteCol: edge.toColumns[i] });
  }
  return pairs;
}

/** Cold-kick de-duplication: one in-flight load per (connection, database). */
const inflightLoads = new Set<string>();

/**
 * When no snapshot exists yet for `(connectionId, database)`, start the
 * engine's schema load (fire-and-forget) so the next code-action compute can
 * read foreign keys. A no-op when the snapshot is warm, when a load is
 * already in flight, or for engines without a schema cache (kusto, azurestorage, mock).
 */
export function requestForeignKeyLoad(
  engineId: DatabaseEngineId,
  connectionId: string,
  database: string | null,
): void {
  if (!database) return;
  if (getSchemaBusSnapshot(connectionId, database)) return;
  const key = `${connectionId}|${database}`;
  if (inflightLoads.has(key)) return;
  const load = startSchemaLoad(engineId, connectionId, database);
  if (!load) return;
  inflightLoads.add(key);
  void load.catch(() => undefined).finally(() => inflightLoads.delete(key));
}

function startSchemaLoad(
  engineId: DatabaseEngineId,
  connectionId: string,
  database: string,
): Promise<unknown> | null {
  switch (engineId) {
    case 'mssql':
      return ensureMssqlTables({ connectionId, database });
    case 'pgsql':
      return ensurePgSchema({ connectionId, database });
    case 'mysql':
      return ensureMysqlSchema({ connectionId, database });
    case 'oracle':
      // Oracle snapshots are per schema; the OE "database" of an Oracle
      // connection is that schema.
      return ensureOracleSchema({ connectionId, schema: database });
    case 'sqlite':
      return ensureSqliteSchema({ connectionId, database });
    case 'kusto':
    case 'azurestorage':
    case 'mock':
      return null;
  }
}

/** Union of two FK result lists, de-duplicated by their column pairs. */
export function mergeForeignKeyResults<T extends ForeignKeyPair>(
  primary: readonly (readonly T[])[],
  secondary: readonly (readonly T[])[],
): readonly (readonly T[])[] {
  const seen = new Set(primary.map(pairsKey));
  const merged = [...primary];
  for (const pairs of secondary) {
    const key = pairsKey(pairs);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(pairs);
  }
  return merged;
}

function pairsKey(pairs: readonly ForeignKeyPair[]): string {
  return pairs.map((p) => `${p.localCol.toLowerCase()}=${p.remoteCol.toLowerCase()}`).join('|');
}
